import React, { FormEvent, useState } from 'react';
import { Box, Text, TextInput, Switch, Button, Checkbox, Alert } from '@razorpay/blade/components';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { useForm } from 'forms';
import { StatutoryContributionsFormData } from './types';
import { useUserId } from '../BankAccountDetails/hooks/useUserId';
import { useToast } from '@razorpay/blade/components';
import { shouldShowPfCeilingNote, validateStatutoryContributions } from './utils';
import { GET_STATUTORY_CONTRIBUTIONS_DATA, portalStatus } from './data';
import { PtStateSelectInput } from './components/PtStateSelectInput';
import { LwfStateSelectInput } from './components/LwfStateSelectInput';
import { EmployeeStatutorySettingsUpdateRequest } from 'api/employeeOnboarding';
import { useMutation, useQuery } from 'react-query';
import employeeOnboarding from 'api/employeeOnboarding';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { parseError } from 'utils/Api';
import DuplicateChecksModal, { DuplicateChecksModalProps } from './components/DuplicateChecksModal';
import { PFRegistrationStatusInfo } from './components/PFRegistrationStatusPFRegistrationStatusInfo';
import { ESICRegistrationStatusInfo } from './components/ESICRegistrationStatusInfo';
import { SectionWrapper } from './components/SectionWrapper';
import { PFStatusSelectInput } from './components/PFStatusSelectInput';
import { ESICStatusSelectInput } from './components/ESICStatusSelectInput';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { OrgSettingDisabledAlert } from './components/OrgSettingDisabledAlert';
import { PFWageCalculationSelectInput } from './components/PfWageCalculationSelectInput';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

import Section from 'components/Kyc/components/Section';

export const StatutoryContributionsForm = ({
  formInitialData,
  onClose,
}: {
  formInitialData: StatutoryContributionsFormData;
  onClose: () => void;
}) => {
  const toast = useToast();
  const userId = useUserId();
  const { isM3S2Enabled } = useIsFeatureDisabledForRearch();

  const currentOrganization = useReduxSelector(loggedInUserSelectors.currentOrganization);
  const pfCeilingDisplay = useReduxSelector(loggedInUserSelectors.pfCeilingDisplay);
  const {
    isPf: canEnablePf,
    isEsi: canEnableEsi,
    isLwf: canEnableLwf,
    isPt: canEnablePt,
  } = currentOrganization || {};

  const { data: statutorySettings } = useQuery({
    queryKey: [GET_STATUTORY_CONTRIBUTIONS_DATA, userId],
    queryFn: () => {
      if (userId) {
        return employeeOnboarding.fetchEmployeeStatutorySettings(userId);
      }
      throw new Error('User ID is required');
    },
    refetchOnMount: false,
  });

  const [isDuplicateChecksModalOpen, setIsDuplicateChecksModalOpen] = useState(false);

  const { validate, formData, setFormData, register, clearFieldError } =
    useForm<StatutoryContributionsFormData>(formInitialData);

  const {
    mutate: updateStatutorySettings,
    isLoading: isUpdatingStatutorySettings,
    error,
  } = useMutation({
    mutationFn: (settings: EmployeeStatutorySettingsUpdateRequest) => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return employeeOnboarding.updateEmployeeStatutorySettings(userId, settings);
    },
    onSuccess: () => {
      toast.show({
        content: 'Statutory Contributions updated successfully',
        color: 'positive',
      });
      setTimeout(() => onClose(), 500);
    },
    onError: (e: typeof AppError) => {
      const isDuplicateCheckError = getAppErrorDetails(e).code === 'USER_DETAILS_ALREADY_EXISTS';
      if (isDuplicateCheckError) {
        setIsDuplicateChecksModalOpen(true);
      }
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
    },
  });

  const clearErrorOnFocus = (fieldName: keyof StatutoryContributionsFormData) =>
    clearFieldError(fieldName);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Perform validation logic here
    const validationResult = await validate(validateStatutoryContributions);
    if (validationResult) {
      updateStatutorySettings({
        tds_settings: {
          is_tds_enabled: formData.isTdsEnabled ?? false,
          aadhaar_number: formData.aadharNumber ?? '',
          pan: formData.isTdsEnabled ? formData.pan ?? '' : '',
        },
        pf_settings: {
          is_pf_enabled: formData.isPfEnabled ?? false,
          pf_status: formData.pfStatus?.[0] ?? '',
          pf_uan: formData.pfUan ?? '',
          is_aadhaar_linked_to_uan: formData.isAadhaarLinkedToUan ?? false,
          include_employer_contribution_in_ctc: formData.includeEmployerContributionInCtc ?? false,
          contribute_to_eps: formData.contributeToEps ?? false,
          pf_wages_calculation_type: formData.pfWagesCalculationType?.[0] ?? undefined,
        },
        esic_settings: {
          is_esic_enabled: formData.isEsicEnabled ?? false,
          esic_status: formData.esicStatus?.[0] ?? '',
          esic_ip_number: formData.esicIpNumber ?? '',
        },
        pt_settings: {
          is_pt_enabled: formData.isPtEnabled ?? false,
          pt_state: formData.ptState?.[0] ?? '',
        },
        lwf_settings: {
          is_lwf_enabled: formData.isLwfEnabled ?? false,
          lwf_location: formData.lwfLocation?.[0] ?? '',
        },
        nps_settings: {
          is_nps_enabled: isM3S2Enabled ? true : formData.isNpsEnabled ?? false,
          nps_pran_number: formData.npsPranNumber ?? '',
        },
      });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const pfRegistrationStatus = statutorySettings?.pf_settings.pf_registration_status;
  const esicRegistrationStatus = statutorySettings?.esic_settings.esic_registration_status;

  const duplicateDetails = getAppErrorDetails(error)?.otherDetails?.jsonResponse
    ?.extraDetails as DuplicateChecksModalProps['details'];

  const shouldShowPfWagesTypeDropdown =
    formData.isPfEnabled &&
    (formData.pfStatus?.[0] === portalStatus.registered ||
      formData.pfStatus?.[0] === portalStatus.notRegistered);

  return (
    <>
      <FormWrapper onSubmit={handleSubmit}>
        <StepContentWrapper maxWidth="700px">
          {/* Tax Deducted at Source Section */}
          <SectionWrapper
            title="Aadhar Number"
            description="Required to ensure a unique, verifiable, and unified identification system across government services.">
            <TextInput
              label="Aadhaar Number"
              placeholder="Enter Aadhaar Number"
              {...register('aadharNumber')}
              onFocus={() => clearErrorOnFocus('aadharNumber')}
              helpText="Aadhaar must be linked to PAN and PF UAN"
            />
          </SectionWrapper>
          <SectionWrapper
            title="Tax Deducted at Source"
            description="Verify identity using Aadhaar and PAN to prevent fraud and ensure compliance.">
            <Box display="flex" alignItems="center" gap="spacing.3" justifyContent="space-between">
              <Text weight="regular">Tax Deducted at Source</Text>
              <Switch
                accessibilityLabel="Toggle Tax Deducted at Source"
                {...register('isTdsEnabled', 'switch')}
              />
            </Box>
            {formData.isTdsEnabled && (
              <TextInput
                label="PAN"
                placeholder="ABCDE1234F"
                necessityIndicator="required"
                {...register('pan')}
                onFocus={() => clearErrorOnFocus('pan')}
                isRequired
              />
            )}
          </SectionWrapper>

          {/* Provident Fund Section */}
          <SectionWrapper
            title="Provident Fund"
            description="An investment fund to serve as long-term savings to support an employee's retirement.">
            <Box display="flex" alignItems="center" gap="spacing.3" justifyContent="space-between">
              <Text weight="regular">Provident Fund</Text>
              <Switch
                accessibilityLabel="Toggle Provident Fund"
                {...register('isPfEnabled', 'switch')}
                isDisabled={canEnablePf === false}
              />
            </Box>
            {canEnablePf === false && (
              <OrgSettingDisabledAlert
                description="PF is turned off for your organisation. To turn it on, go to your settings."
                linkText="Provident Fund"
              />
            )}
            {formData.isPfEnabled && (
              <PFStatusSelectInput register={register} formData={formData} />
            )}

            {formData.isPfEnabled && formData.pfStatus?.[0] === portalStatus.registered && (
              <TextInput
                label="UAN"
                placeholder="Enter UAN Number"
                {...register('pfUan')}
                onFocus={() => clearErrorOnFocus('pfUan')}
                helpText="Aadhaar must be linked to UAN"
                isRequired
                necessityIndicator="required"
              />
            )}

            {Boolean(shouldShowPfWagesTypeDropdown) && (
              <PFWageCalculationSelectInput registerFormData={register} />
            )}

            {pfCeilingDisplay.note &&
              shouldShowPfCeilingNote({
                formData,
                isOrgPfLimitEnabled: !!currentOrganization?.paymentOptions?.restrictPfToLimit,
              }) && (
                <Alert
                  title="Note"
                  description={pfCeilingDisplay.note}
                  color="information"
                  isDismissible={false}
                  isFullWidth
                />
              )}

            {formData.isPfEnabled &&
              (formData.pfStatus?.[0] === portalStatus.registered ||
                formData.pfStatus?.[0] === portalStatus.notRegistered) && (
                <>
                  {formData.pfStatus?.[0] === portalStatus.registered && (
                    <Checkbox
                      {...register('isAadhaarLinkedToUan', 'checkbox')}
                      isChecked={formData.isAadhaarLinkedToUan}
                      helpText="Note: If Aadhaar not linked, PF payment cannot be processed.">
                      Aadhaar linked to UAN
                    </Checkbox>
                  )}
                  <Checkbox
                    {...register('includeEmployerContributionInCtc', 'checkbox')}
                    isChecked={formData.includeEmployerContributionInCtc}>
                    Include Employer Contribution to PF in Employee's CTC
                  </Checkbox>
                  <Checkbox
                    {...register('contributeToEps', 'checkbox')}
                    isChecked={formData.contributeToEps}>
                    Contribute to Employee Pension Scheme
                  </Checkbox>
                </>
              )}

            {formData.isPfEnabled && (
              <PFRegistrationStatusInfo
                pfRegistrationStatus={pfRegistrationStatus}
                pfStatus={formData.pfStatus?.[0]}
              />
            )}
          </SectionWrapper>

          {/* Employees State Insurance Corporation */}
          <SectionWrapper
            title="Employees State Insurance Corporation"
            description="A social security scheme that benefits employees for health related problems.">
            <Box display="flex" alignItems="center" gap="spacing.3" justifyContent="space-between">
              <Text weight="regular">Employees State Insurance Corporation</Text>
              <Switch
                accessibilityLabel="Toggle Employees State Insurance Corporation"
                {...register('isEsicEnabled', 'switch')}
                isDisabled={canEnableEsi === false}
              />
            </Box>

            {canEnableEsi === false && (
              <OrgSettingDisabledAlert
                description="ESIC is turned off for your organisation. To turn it on, go to your settings."
                linkText="Employees State Insurance"
              />
            )}
            {formData.isEsicEnabled && (
              <ESICStatusSelectInput register={register} formData={formData} />
            )}

            {formData.isEsicEnabled && formData.esicStatus?.[0] === portalStatus.registered && (
              <TextInput
                label="ESIC IP Number"
                placeholder="Enter ESIC IP Number"
                {...register('esicIpNumber')}
                onFocus={() => clearErrorOnFocus('esicIpNumber')}
                isRequired
                necessityIndicator="required"
              />
            )}

            {formData.isEsicEnabled && (
              <ESICRegistrationStatusInfo
                esicRegistrationStatus={esicRegistrationStatus}
                esicStatus={formData.esicStatus?.[0]}
              />
            )}
          </SectionWrapper>

          {/* Professional Tax */}
          <SectionWrapper
            title="Professional Tax"
            description="Salaried individuals are liable to pay a part of their salary to state governments as tax.">
            <Box display="flex" alignItems="center" gap="spacing.3" justifyContent="space-between">
              <Text weight="regular">Professional Tax</Text>
              <Switch
                accessibilityLabel="Toggle Professional Tax"
                {...register('isPtEnabled', 'switch')}
                isDisabled={canEnablePt === false}
              />
            </Box>

            {canEnablePt === false && (
              <OrgSettingDisabledAlert
                description="PT is turned off for your organisation. To turn it on, go to your settings."
                linkText="Professional Tax"
              />
            )}
            {formData.isPtEnabled && (
              <PtStateSelectInput registerFormData={register} formData={formData} />
            )}
          </SectionWrapper>

          {/* Labour Welfare Fund */}
          <SectionWrapper
            title="Labour Welfare Fund"
            description="Collected to fund welfare measures for workers in certain states.">
            <Box display="flex" alignItems="center" gap="spacing.3" justifyContent="space-between">
              <Text weight="regular">Labour Welfare Fund</Text>
              <Switch
                accessibilityLabel="Toggle Labour Welfare Fund"
                isChecked={formData.isLwfEnabled}
                onChange={({ isChecked }) => setFormData({ ...formData, isLwfEnabled: isChecked })}
                isDisabled={canEnableLwf === false}
              />
            </Box>

            {canEnableLwf === false && (
              <OrgSettingDisabledAlert
                description="LWF is turned off for your organisation. To turn it on, go to your settings."
                linkText="Labour Welfare Fund"
              />
            )}
            {formData.isLwfEnabled && (
              <LwfStateSelectInput registerFormData={register} formData={formData} />
            )}

            {formData.isLwfEnabled &&
              formData.ptState?.[0] &&
              formData.lwfLocation?.[0] &&
              formData.ptState?.[0] !== formData.lwfLocation?.[0] && (
                <Alert
                  description="Warning: LWF location does not match PT location! Proceed with caution."
                  color="notice"
                  isDismissible={false}
                />
              )}
          </SectionWrapper>

          {/* National Pension Scheme */}
          <SectionWrapper
            title="National Pension Scheme"
            description="Government-backed retirement savings scheme for post-retirement security."
            showDivider={false}>
            {!isM3S2Enabled && (
              <Box
                display="flex"
                alignItems="center"
                gap="spacing.3"
                justifyContent="space-between">
                <Text weight="regular">National Pension Scheme</Text>
                <Switch
                  accessibilityLabel="Toggle National Pension Scheme"
                  isChecked={formData.isNpsEnabled}
                  onChange={({ isChecked }) =>
                    setFormData({ ...formData, isNpsEnabled: isChecked })
                  }
                />
              </Box>
            )}
            {(formData.isNpsEnabled || isM3S2Enabled) && (
              <TextInput
                label="PRAN"
                placeholder="Enter PRAN Number"
                {...register('npsPranNumber')}
                onFocus={() => clearErrorOnFocus('npsPranNumber')}
              />
            )}
          </SectionWrapper>
        </StepContentWrapper>

        <StepActionsWrapper>
          <Button
            variant="tertiary"
            isDisabled={isUpdatingStatutorySettings}
            onClick={handleCancel}
            type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isUpdatingStatutorySettings}>
            Save
          </Button>
        </StepActionsWrapper>
      </FormWrapper>
      <DuplicateChecksModal
        isOpen={isDuplicateChecksModalOpen}
        onDismiss={() => setIsDuplicateChecksModalOpen(false)}
        details={duplicateDetails}
      />
    </>
  );
};
